import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
    IApiResponse,
    IPaginatedApiResponse,
} from "../../../utils/types/api-response";
import {
    IActiveProvider,
    IProvider,
    IProviderCaseload,
    IProviderCaseloadFilters,
    IProvidersFilterTypes,
    IProviderUpcomingSession,
    IProviderUpcomingSessionsFilters,
    ISuperviseeCaseload,
    ISuperviseeCaseloadFilters,
} from "../types/providers.types";
import { handleRemoveEmptyParamFromQueryParams } from "../../../utils/helpers";
import {
    fetchActiveProviders,
    fetchProviderCaseload,
    fetchProviders,
    fetchProviderUpcomingSessions,
    fetchSuperviseesCaseload,
} from "../services/providers.services";
import { useDoesUserHavePermission } from "../../../hooks/permissions";
import { UserPermisions } from "../../../utils/types/user";

// Fetch provider's caseload
export function useFetchProvidersCaseload(params: IProviderCaseloadFilters) {
    return useQuery<
        IPaginatedApiResponse<IProviderCaseload[]>,
        AxiosError<IApiResponse<IProviderCaseload[]>>
    >({
        queryKey: [
            `caseload`,
            params.providerId,
            handleRemoveEmptyParamFromQueryParams({
                ...params,
            }),
        ],
        queryFn: () => fetchProviderCaseload(params),
        enabled: params.enabled,
    });
}

// Fetch supervisees caseload
export function useFetchSuperviseesCaseload(
    filters: ISuperviseeCaseloadFilters
) {
    return useQuery<
        IPaginatedApiResponse<ISuperviseeCaseload[]>,
        AxiosError<IApiResponse<ISuperviseeCaseload[]>>
    >({
        queryKey: [
            filters.providerId,
            `supervisees-caseload`,
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => fetchSuperviseesCaseload(filters),
    });
}

export function useFetchProviders(
    filters: IProvidersFilterTypes,
    enabled?: boolean
) {
    const canViewProvidersList = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_PROVIDER_READ,
        UserPermisions.CLIENT_INFO_PROVIDER_WRITE,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.PROVIDER_INFO_READ,
        UserPermisions.CLIENT_INFO_INVOICE_WRITE,
    ]);

    return useQuery<
        IPaginatedApiResponse<IProvider[]>,
        AxiosError<IApiResponse<IProvider[]>>
    >({
        queryKey: ["providers", handleRemoveEmptyParamFromQueryParams(filters)],
        queryFn: () => fetchProviders(filters),
        enabled:
            enabled === undefined
                ? canViewProvidersList
                : enabled && canViewProvidersList,
    });
}

export function useFetchActiveProviders(providerName?: string) {
    return useQuery<
        IPaginatedApiResponse<IActiveProvider[]>,
        AxiosError<IPaginatedApiResponse<IActiveProvider[]>>
    >({
        queryKey: ["active-providers", providerName],
        queryFn: () => fetchActiveProviders(providerName),
    });
}

export function useFetchProviderUpcomingSessions(
    providerId: string,
    filters: IProviderUpcomingSessionsFilters,
    enabled?: boolean
) {
    return useQuery<
        IPaginatedApiResponse<IProviderUpcomingSession[]>,
        AxiosError<IPaginatedApiResponse<IProviderUpcomingSession[]>>
    >({
        queryKey: [
            "provider-upcoming-sessions",
            providerId,
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => fetchProviderUpcomingSessions(providerId, filters),
        enabled,
    });
}
